<template>
  <div class="KlarnaConfirmation">
    <!-- Loading state -->
    <div v-if="isLoading" class="klarna-loading flex items-center justify-center py-32">
      <div class="loading-spinner" />
      <span class="ml-12 type-sm text-text-subtle">{{ loadingText }}</span>
    </div>

    <!-- Error state -->
    <div v-else-if="errorMessage" class="klarna-error p-16 bg-bg-error rounded-8">
      <p class="type-sm text-text-error">{{ errorMessage }}</p>
    </div>

    <!-- Klarna Confirmation container -->
    <div v-else-if="hasSnippet" class="klarna-confirmation-wrapper">
      <div
        ref="klarnaContainer"
        class="klarna-confirmation-container"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
import useNorceCheckoutApi from './useNorceCheckoutApi';

const props = withDefaults(defineProps<{
  orderId: string;
  paymentId: string;
  loadingText?: string;
  errorText?: string;
}>(), {
  loadingText: 'Loading confirmation...',
  errorText: 'Could not load payment confirmation.',
});

const api = useNorceCheckoutApi();
const klarnaContainer = ref<HTMLElement | null>(null);
const isLoading = ref(true);
const hasSnippet = ref(false);
const htmlSnippet = ref<string | null>(null);
const errorMessage = ref<string | null>(null);

/**
 * Render the Klarna Confirmation widget
 * Similar to checkout snippet, needs script execution handling
 */
const renderKlarnaConfirmation = async () => {
  if (!htmlSnippet.value || !klarnaContainer.value) {
    return;
  }

  try {
    // Clear previous content
    klarnaContainer.value.innerHTML = '';

    // Insert the HTML snippet (scripts will be in DOM but not executed)
    klarnaContainer.value.innerHTML = htmlSnippet.value;

    // Get all script tags and convert to static array
    const scriptTags = Array.from(klarnaContainer.value.getElementsByTagName('script'));

    // Execute scripts manually
    for (const oldScript of scriptTags) {
      const parentNode = oldScript.parentNode;

      if (!parentNode) continue;

      const newScript = document.createElement('script');
      newScript.type = 'text/javascript';
      newScript.text = oldScript.text;

      // Copy attributes
      Array.from(oldScript.attributes).forEach((attr) => {
        if (attr.name !== 'type') {
          newScript.setAttribute(attr.name, attr.value);
        }
      });

      parentNode.removeChild(oldScript);
      parentNode.appendChild(newScript);
    }

    await nextTick();
  } catch (e) {
    console.error('Klarna Confirmation render error:', e);
  }
};

onMounted(async () => {
  if (!props.orderId || !props.paymentId) {
    isLoading.value = false;
    return;
  }

  try {
    const response = await api.getKlarnaConfirmation(props.orderId, props.paymentId);

    if (response?.payment?.htmlSnippet) {
      htmlSnippet.value = response.payment.htmlSnippet;
      hasSnippet.value = true;
      isLoading.value = false;

      await nextTick();
      renderKlarnaConfirmation();
    } else {
      isLoading.value = false;
      // Don't show error if there's simply no snippet - the order completed successfully
    }
  } catch (e) {
    console.error('Failed to load Klarna confirmation:', e);
    errorMessage.value = props.errorText;
    isLoading.value = false;
  }
});

// Cleanup on unmount
onBeforeUnmount(() => {
  if (klarnaContainer.value) {
    klarnaContainer.value.innerHTML = '';
  }
});
</script>

<style scoped lang="postcss">
.KlarnaConfirmation {
  @apply w-full;
}

.klarna-confirmation-wrapper {
  @apply relative;
}

.klarna-confirmation-container {
  @apply w-full;
}

.klarna-loading {
  @apply min-h-[100px];
}

.loading-spinner {
  @apply w-24 h-24 border-2 border-light border-t-dark animate-spin;
  border-radius: 50%;
}
</style>
